import { createContext, useContext, useEffect, useRef } from 'react';

/**
 * "Go to page X and open Y there" — how the navbar search (and anything
 * else) hands a page a job to do on arrival, without a router.
 *
 *   navigate('student-directory', { studentId: 782, tab: 'payments' })
 *
 * App keeps the one pending intent; the target page reads it with
 * useNavIntent and applies it once its own data has loaded:
 *
 *   useNavIntent('teachers', (intent) => {
 *     const t = rows.find((r) => r.id === intent.teacherId);
 *     if (!t) return false;          // not loaded yet — try again later
 *     openEdit(t);                   // anything else = handled
 *   }, [rows]);
 *
 * Returning `false` keeps the intent pending, so a page whose data is
 * still loading picks it up on the render the rows arrive. Each intent
 * carries a nonce, so choosing the same result twice acts twice.
 */
export const NavIntentContext = createContext({ intent: null, done: () => {}, navigate: () => {} });

/** `navigate(pageId, intent?)` from inside any page. */
export function useNavigate() {
  return useContext(NavIntentContext).navigate;
}

export function useNavIntent(pageId, apply, deps = []) {
  const { intent, done } = useContext(NavIntentContext);
  const applyRef = useRef(apply);
  applyRef.current = apply;

  useEffect(() => {
    if (!intent || intent.page !== pageId) return;
    if (applyRef.current(intent) === false) return;
    done(intent.nonce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent, pageId, done, ...deps]);
}
